import React from "react";
import { MoonLoader } from "react-spinners";
import {
  EnrichedAppointment,
  formatLocalTime,
  formatCountdown,
} from "../../utils/schedule";
import type { ScheduleStatus } from "../../hooks/useProviderSchedule";
import { recordRecentPatient } from "../../utils/recentPatients";

// item.name falls back to the patientId (or "Unknown patient") when no real
// name is known — don't persist those as if they were a display name.
const recentPatientName = (item: EnrichedAppointment): string | undefined =>
  item.name && item.name !== item.patientId ? item.name : undefined;

interface ScheduleColumnProps {
  // Current + upcoming appointments (completed ones live in the side column).
  upcoming: EnrichedAppointment[];
  now: Date;
  status: ScheduleStatus;
  errorMsg: string | null;
  // True once the schedule has loaded but contains no appointments at all
  // (so the "no upcoming" copy distinguishes an empty day from a filtered list).
  isEmpty: boolean;
  onSelectPatient: (patientId: string, encounterId?: string) => void;
}

const ScheduleColumn: React.FC<ScheduleColumnProps> = ({
  upcoming,
  now,
  status,
  errorMsg,
  isEmpty,
  onSelectPatient,
}) => {
  const currentIndex = upcoming.findIndex(
    (e) => e.start && e.end && now >= e.start && now < e.end,
  );
  const nextIndex = upcoming.findIndex((e) => e.start && e.start > now);
  // The appointment to feature in the top card and highlight in the list.
  const featuredIndex = currentIndex !== -1 ? currentIndex : nextIndex;

  return (
    <div className="flex flex-col gap-6">
      <section data-testid="my-schedule">
        <h2 className="text-xl font-bold text-gray-800 mb-4">My Schedule</h2>

        {status === "loading" && (
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <MoonLoader size={16} color="#7a46bb" loading={true} />
            Loading schedule…
          </div>
        )}

        {status === "error" && (
          <p role="alert" className="text-sm text-red-600">
            {errorMsg ?? "Could not load your provider schedule."}
          </p>
        )}

        {status === "loaded" && isEmpty && (
          <p className="text-sm text-gray-500">
            No appointments scheduled for today.
          </p>
        )}

        {status === "loaded" && !isEmpty && upcoming.length === 0 && (
          <p className="text-sm text-gray-500">No upcoming appointments.</p>
        )}

        {status === "loaded" && featuredIndex !== -1 && (
          <FeaturedCard
            item={upcoming[featuredIndex]}
            isCurrent={currentIndex !== -1}
            now={now}
            onSelectPatient={onSelectPatient}
          />
        )}

        {status === "loaded" && upcoming.length > 0 && (
          <>
            <h3 className="text-base font-bold text-gray-800 mt-6 mb-3">
              Upcoming Encounters
            </h3>
            <ul className="flex flex-col gap-3">
              {upcoming.map((item, index) => (
                <ScheduleRow
                  key={
                    item.appt.appointmentid ??
                    item.appt.encounterid ??
                    `${item.patientId ?? "x"}-${item.appt.starttime ?? index}`
                  }
                  item={item}
                  highlighted={index === featuredIndex}
                  onSelectPatient={onSelectPatient}
                />
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
};

const ClockIcon: React.FC = () => (
  <svg
    className="h-4 w-4 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
  </svg>
);

interface RowProps {
  item: EnrichedAppointment;
  onSelectPatient: (patientId: string, encounterId?: string) => void;
}

const FeaturedCard: React.FC<RowProps & { isCurrent: boolean; now: Date }> = ({
  item,
  isCurrent,
  now,
  onSelectPatient,
}) => {
  const timeRange =
    item.start && item.end
      ? `${formatLocalTime(item.start)} - ${formatLocalTime(item.end)}`
      : "—";
  const canJoin = !!item.patientId;
  return (
    <div
      className={`rounded-lg border p-4 flex items-center justify-between gap-4 ${
        isCurrent
          ? "bg-green-50 border-green-500"
          : "bg-purple-50 border-purple-300"
      }`}
    >
      <div className="min-w-0">
        <span
          className={`text-xs font-bold uppercase tracking-wide ${
            isCurrent ? "text-green-700" : "text-purple-700"
          }`}
        >
          {isCurrent && item.start
            ? "NOW"
            : item.start
              ? formatCountdown(item.start, now)
              : "Next"}
        </span>
        <p className="text-base font-semibold text-gray-900 truncate">
          {item.name}
        </p>
        <p className="text-sm text-gray-500">{item.apptType}</p>
        <p className="text-sm text-gray-500">
          Athena ID: {item.patientId ?? "N/A"}
        </p>
        <p className="text-sm text-gray-500">Time: {timeRange}</p>
      </div>
      <button
        type="button"
        disabled={!canJoin}
        onClick={() => {
          /* v8 ignore next -- button is disabled (unclickable) when patientId is absent */
          if (!item.patientId) return;
          recordRecentPatient(item.patientId, recentPatientName(item));
          onSelectPatient(item.patientId, item.encounterId);
        }}
        className={`shrink-0 px-4 py-2 text-sm font-semibold text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-default ${
          isCurrent
            ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
            : "bg-gray-900 hover:bg-black focus:ring-gray-700"
        }`}
      >
        {isCurrent ? "JOIN ENCOUNTER" : "Join"}
      </button>
    </div>
  );
};

const ScheduleRow: React.FC<RowProps & { highlighted: boolean }> = ({
  item,
  highlighted,
  onSelectPatient,
}) => {
  const canJoin = !!item.patientId;
  return (
    <li
      className={`rounded-lg border bg-white flex items-center justify-between gap-3 px-4 py-3 ${
        highlighted
          ? "border-green-500 ring-1 ring-green-500"
          : "border-gray-200"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex flex-col items-center w-16 shrink-0">
          <ClockIcon />
          <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
            {item.start ? formatLocalTime(item.start) : "—"}
          </span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {item.name}
          </p>
          <p className="text-sm text-gray-500 truncate">{item.apptType}</p>
          <p className="text-xs text-gray-400">
            Athena ID: {item.patientId ?? "N/A"}
          </p>
        </div>
      </div>
      <button
        type="button"
        disabled={!canJoin}
        onClick={() => {
          /* v8 ignore next -- button is disabled (unclickable) when patientId is absent */
          if (!item.patientId) return;
          recordRecentPatient(item.patientId, recentPatientName(item));
          onSelectPatient(item.patientId, item.encounterId);
        }}
        className="shrink-0 px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-md hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 disabled:opacity-50 disabled:cursor-default"
      >
        Join
      </button>
    </li>
  );
};

export default ScheduleColumn;
